import moment, { Moment } from "moment";

import { CoverCropDecision, PendingCoverCropSuggestion } from "services";

import { getMiddayMomentDate } from "utils";

/** What the farmer answered for "Which cover crop type did you grow?". */
export type CoverCropType = "simple" | "complex" | "none";

/**
 * One field-year's in-progress answers.
 *
 * Held in the drawer root and keyed by detectionId — never inside a year tab or a card. detectionIds are
 * unique across years, so switching tabs is a pure render filter and unsaved answers cannot be dropped.
 */
export type DraftDecision = {
  type?: CoverCropType;
  seedingStart?: Moment;
  seedingEnd?: Moment;
  harvestStart?: Moment;
  harvestEnd?: Moment;
  hasResidue?: boolean;
};

export type DraftsByDetectionId = Record<string, DraftDecision>;

export type CoverCropCardState = "pending" | "complete" | "skipped";

export const isDraftAbsent = (draft?: DraftDecision): boolean => draft?.type === "none";

export const isDraftComplete = (draft?: DraftDecision): boolean => {
  if (!draft?.type) return false;
  if (isDraftAbsent(draft)) return true;

  return !!draft.seedingStart && !!draft.seedingEnd && !!draft.harvestStart && !!draft.harvestEnd && draft.hasResidue !== undefined;
};

// The absent check comes first: "no cover crop" is a complete, submittable answer, so isDraftComplete
// would otherwise claim it as complete.
export const getCardState = (draft?: DraftDecision): CoverCropCardState => {
  if (isDraftAbsent(draft)) return "skipped";
  if (isDraftComplete(draft)) return "complete";

  return "pending";
};

export const countCompleteDrafts = (suggestions: PendingCoverCropSuggestion[], drafts: DraftsByDetectionId): number =>
  suggestions.filter((suggestion) => isDraftComplete(drafts[suggestion.detectionId])).length;

/** Season years present in the suggestion list, oldest first — the drawer's tab order. */
export const getSeasonYears = (suggestions: PendingCoverCropSuggestion[]): number[] =>
  [...new Set(suggestions.map(({ seasonYear }) => seasonYear))].sort((a, b) => a - b);

export const getSeedingPickerOpenDate = ({ obsWindowStart, seasonYear }: PendingCoverCropSuggestion): Moment => {
  const observationWindowStart = moment(obsWindowStart, "YYYY-MM-DD", true);

  return observationWindowStart.isValid() ? observationWindowStart : moment(`${seasonYear}-08-01`, "YYYY-MM-DD");
};

const toMiddayIsoString = (date?: Moment): string | undefined => (date ? getMiddayMomentDate(date)?.toISOString() : undefined);

export const getFieldIds = (suggestions: PendingCoverCropSuggestion[]): string[] => [...new Set(suggestions.map(({ fieldId }) => fieldId))];

/**
 * Builds the single request body for every complete answer across every year.
 *
 * Partially filled cards are omitted rather than rejected: the farmer can leave a field half-answered,
 * save what they did finish, and come back to the rest.
 */
export const buildDecisionsPayload = (suggestions: PendingCoverCropSuggestion[], drafts: DraftsByDetectionId): CoverCropDecision[] =>
  suggestions.flatMap((suggestion): CoverCropDecision[] => {
    const draft = drafts[suggestion.detectionId];

    if (!isDraftComplete(draft)) return [];

    if (isDraftAbsent(draft)) {
      return [{ detectionId: suggestion.detectionId, decision: "confirmed_absent" }];
    }

    return [
      {
        detectionId: suggestion.detectionId,
        decision: "confirmed",
        mixType: draft.type as "simple" | "complex",
        seedingStart: toMiddayIsoString(draft.seedingStart),
        seedingEnd: toMiddayIsoString(draft.seedingEnd),
        harvestStart: toMiddayIsoString(draft.harvestStart),
        harvestEnd: toMiddayIsoString(draft.harvestEnd),
        hasResidue: draft.hasResidue,
      },
    ];
  });
